// Chord mastery atoms: an individual chord (root pitch-class × quality) as a
// masterable unit, derived — never a hand-kept list. A chord drill grants the
// atom; a song lists the atoms for the chords it contains. Pure leaf.
import type { DrillChord } from "./arranger";
import type { ChordQuality } from "./note-math";
import { isScaleAtom } from "./scale-atoms";
import { isSkillId } from "./skills";

// Pitch-class → kebab name (flats for black keys; "b-flat" reads cleanly).
const PC_NAME = [
  "c",
  "d-flat",
  "d",
  "e-flat",
  "e",
  "f",
  "g-flat",
  "g",
  "a-flat",
  "a",
  "b-flat",
  "b",
] as const;

const ATOM_RE = /^chord-(c|d-flat|d|e-flat|e|f|g-flat|g|a-flat|a|b-flat|b)-(maj|min|dom7)$/;

/** The atom id for a chord rooted at `rootMidi` with `quality`. */
export function chordAtomId(rootMidi: number, quality: ChordQuality): string {
  const pc = ((Math.round(rootMidi) % 12) + 12) % 12;
  // `?? "c"` is unreachable (pc is always 0–11) — it only satisfies
  // noUncheckedIndexedAccess.
  return `chord-${PC_NAME[pc] ?? "c"}-${quality}`;
}

/** Whether `id` is a well-formed chord-atom id. */
export function isChordAtom(id: string): boolean {
  return ATOM_RE.test(id);
}

const PC_BY_NAME = new Map<string, number>(PC_NAME.map((n, i) => [n, i]));

/** Inverse of `chordAtomId`: a chord-atom id → `{ root: pitch-class, quality }`, or null if
 *  malformed. Octave is lost (only the pitch-class is encoded) — fine for degree/progression
 *  analysis, which is octave-agnostic. */
export function chordFromAtom(atom: string): DrillChord | null {
  const m = ATOM_RE.exec(atom);
  const root = m ? PC_BY_NAME.get(m[1] ?? "") : undefined;
  if (m === null || root === undefined) return null;
  return { root, quality: m[2] as ChordQuality };
}

/** Deduped atom ids for a chord-per-bar list. */
export function chordAtoms(chords: readonly DrillChord[]): string[] {
  return [...new Set(chords.map((c) => chordAtomId(c.root, c.quality)))];
}

const PRETTY_ROOT: Record<string, string> = {
  c: "C",
  "d-flat": "D♭",
  d: "D",
  "e-flat": "E♭",
  e: "E",
  f: "F",
  "g-flat": "G♭",
  g: "G",
  "a-flat": "A♭",
  a: "A",
  "b-flat": "B♭",
  b: "B",
};
const PRETTY_QUAL: Record<string, string> = { maj: "", min: "m", dom7: "7" };

/** Display name for a chord-atom id, e.g. `chord-g-dom7` → "G7", `chord-d-min` → "Dm". */
export function chordLabel(id: string): string {
  const m = /^chord-(.+)-(maj|min|dom7)$/.exec(id);
  if (!m) return id;
  return `${PRETTY_ROOT[m[1] ?? ""] ?? m[1]}${PRETTY_QUAL[m[2] ?? ""] ?? ""}`;
}

/** A legal curriculum-tag id: a technique skill, a chord atom, or a scale atom. */
export function isAtomId(id: string): boolean {
  return isSkillId(id) || isChordAtom(id) || isScaleAtom(id);
}
